import dayjs from 'dayjs';
import { ref, reactive, WritableComputedRef, isRef, getCurrentScope, onScopeDispose } from 'vue';
import { Position, Size, TriggerPathType } from '@/ai/utils/types';
import { MIN_WIDTH, MIN_HEIGHT, MAX_WIDTH } from '@/ai/utils/defined';

/**
 * 챗봇 메시지별 주고받은 시각 표시용 — createdAt(ISO-8601 UTC, Z) 을 로컬 HH:mm 으로.
 * 서버 메시지는 DB 저장 시각, 신규(전송 중) 메시지는 client now (chat store addMessage 부여).
 */
export const formatMessageTime = (iso?: string): string => {
  if (!iso) return '';
  const d = dayjs(iso);
  return d.isValid() ? d.format('HH:mm') : '';
};

/** 메시지 시각 툴팁용 — 로컬 YYYY-MM-DD HH:mm:ss. */
export const formatMessageTimeFull = (iso?: string): string => {
  if (!iso) return '';
  const d = dayjs(iso);
  return d.isValid() ? d.format('YYYY-MM-DD HH:mm:ss') : '';
};

import {
  getWasTabPanelInfo,
  getPodTabPanelInfo,
  getNameSpaceTabPanelInfo,
  getNodeTabPanelInfo,
  getHpaTabPanelInfo,
  getPvcTabPanelInfo,
  getDbTabPanelInfo,
  getHostTabPanelInfo,
  getHostProcessTabPanelInfo,
  getContainerTabPanelInfo,
  getNetworkDeviceTabPanelInfo,
  getNetworkObjectTabPanelInfo,
  getNetworkInterfaceTabPanelInfo,
  getKafkaClusterTabPanelInfo,
  getKafkaBrokerTabPanelInfo,
  getKafkaTopicTabPanelInfo,
  getServiceTabPanelInfo,
  getAlertDetailPanelInfo,
  getSqlTabPanelInfo,
  getSessionTabPanelInfo,
  getActiveTxnTabPanelInfo,
  getBrokerTabPanelInfo,
  // getUserMonitoringSessionAnalysisTabPanelInfo,
  getUrlMonitoringTabPanelInfo,
  getStatefulSetTabPanelInfo,
  getReplicaSetTabPanelInfo,
  getConfigMapTabPanelInfo,
  getRoleBindingTabPanelInfo,
  getClusterRolesTabPanelInfo,
  getClusterRolesBindingsTabPanelInfo,
  getServiceAccountsTabPanelInfo,
  getDeploymentTabPanelInfo,
  getDaemonSetTabPanelInfo,
  getCronJobTabPanelInfo,
  getClusterTabPanelInfo,
  getJobTabPanelInfo,
  getWebServerTabPanelInfo,
  getCloudServiceTabPanelInfo,
  getHostProcessDetailTabPanelInfo,
  getOpenShiftRouteTabPanelInfo,
  getServiceDetailTabPanelInfo,
} from '@/common/uses/useSlideDetail';

export interface DetailInfo {
  targetId: string;
  targetName?: string;
  addition?: Record<string, any>;
}

export interface AddTabPanelInfo {
  type: string;
  name: string;
  addition?: Record<string, any>;
  state?: Record<string, any>;
}

const calculatePosition = ({
  elementSize,
  position,
}: {
  elementSize: Size;
  position: Position;
}): Position => {
  const { innerWidth, innerHeight } = window;

  const top = Math.max(0, Math.min(innerHeight - elementSize.height, position.top));
  const left = Math.max(0, Math.min(innerWidth - elementSize.width, position.left));

  return { top, left };
};

export const useDrag = ({
  size,
  position,
}: {
  size: WritableComputedRef<Size> | Size;
  position: WritableComputedRef<Position>;
}) => {
  const dragStart = reactive({ x: 0, y: 0 });
  const isDragging = ref(false);

  const getElementSize = (): Size => {
    return isRef(size) ? size.value : size;
  };

  const onDrag = (event: PointerEvent) => {
    const deltaX = event.clientX - dragStart.x;
    const deltaY = event.clientY - dragStart.y;

    const newPosition = {
      top: position.value.top + deltaY,
      left: position.value.left + deltaX,
    };

    position.value = calculatePosition({
      elementSize: getElementSize(),
      position: newPosition,
    });

    dragStart.x = event.clientX;
    dragStart.y = event.clientY;
  };

  const stopDrag = () => {
    if (isDragging.value) {
      document.removeEventListener('pointermove', onDrag);
      document.removeEventListener('pointerup', stopDrag);
      isDragging.value = false;
    }
  };

  const startDrag = (event: MouseEvent) => {
    if (isDragging.value) return;
    isDragging.value = true;

    dragStart.x = event.clientX;
    dragStart.y = event.clientY;

    document.addEventListener('pointermove', onDrag);
    document.addEventListener('pointerup', stopDrag);
  };

  // 드래그 도중 컴포넌트가 해제되면 document 리스너가 남는다 — 스코프 종료 시 정리 (리뷰 지적).
  if (getCurrentScope()) {
    onScopeDispose(stopDrag);
  }

  return {
    startDrag,
    stopDrag,
  };
};

export const useResize = ({
  size,
  position,
}: {
  size: WritableComputedRef<Size>;
  position: WritableComputedRef<Position>;
}) => {
  const direction = ref<string>('');
  const dragStart = reactive({ x: 0, y: 0 });
  const isResizing = ref(false);
  let pendingSize = { width: 0, height: 0 };
  let pendingPosition = { top: 0, left: 0 };
  let animationFrame: number | null = null;

  const onResize = (event: PointerEvent) => {
    const deltaX = event.clientX - dragStart.x;
    const deltaY = event.clientY - dragStart.y;

    pendingSize = { ...size.value };
    pendingPosition = { ...position.value };

    if (direction.value.includes('right')) {
      pendingSize.width = Math.min(Math.max(MIN_WIDTH, size.value.width + deltaX), MAX_WIDTH);
    }

    if (direction.value.includes('left')) {
      const potentialWidth = size.value.width - deltaX;
      if (potentialWidth >= MIN_WIDTH && potentialWidth <= MAX_WIDTH) {
        pendingSize.width = potentialWidth;
        pendingPosition.left += deltaX;
      }
    }

    if (direction.value.includes('top')) {
      const potentialHeight = size.value.height - deltaY;
      if (potentialHeight >= MIN_HEIGHT && potentialHeight <= window.innerHeight) {
        pendingSize.height = potentialHeight;
        pendingPosition.top += deltaY;
      }
    }

    if (direction.value.includes('bottom')) {
      pendingSize.height = Math.min(
        Math.max(MIN_HEIGHT, size.value.height + deltaY),
        window.innerHeight - position.value.top,
      );
    }

    if (!animationFrame) {
      animationFrame = requestAnimationFrame(() => {
        size.value = { ...pendingSize };
        position.value = calculatePosition({
          elementSize: pendingSize,
          position: pendingPosition,
        });

        animationFrame = null;
      });
    }

    dragStart.x = event.clientX;
    dragStart.y = event.clientY;
  };

  const stopResize = () => {
    if (isResizing.value) {
      document.removeEventListener('pointermove', onResize);
      document.removeEventListener('pointerup', stopResize);
      isResizing.value = false;
    }
  };

  const startResize = (event: MouseEvent, dir: string) => {
    event.preventDefault();
    if (!['top', 'bottom', 'left', 'right'].some((d) => dir.includes(d))) return;
    if (isResizing.value) return;

    direction.value = dir;
    dragStart.x = event.clientX;
    dragStart.y = event.clientY;
    isResizing.value = true;

    document.addEventListener('pointermove', onResize);
    document.addEventListener('pointerup', stopResize);
  };

  // 리사이즈 도중 컴포넌트가 해제되면 document 리스너/대기 중 RAF 가 남는다 — 스코프 종료 시 정리.
  if (getCurrentScope()) {
    onScopeDispose(() => {
      stopResize();
      if (animationFrame !== null) {
        cancelAnimationFrame(animationFrame);
        animationFrame = null;
      }
    });
  }

  return {
    startResize,
    stopResize,
  };
};

export const detailTabPanelInfo: Record<
  TriggerPathType,
  (detailInfo: DetailInfo) => Promise<AddTabPanelInfo | null>
> = {
  wasInfo: getWasTabPanelInfo,
  podInfo: getPodTabPanelInfo,
  namespaceInfo: getNameSpaceTabPanelInfo,
  nodeInfo: getNodeTabPanelInfo,
  hpaInfo: getHpaTabPanelInfo,
  pvcInfo: getPvcTabPanelInfo,
  dbInfo: getDbTabPanelInfo,
  hostInfo: getHostTabPanelInfo,
  processInfo: getHostProcessTabPanelInfo,
  processDetailInfo: getHostProcessDetailTabPanelInfo,
  containerInfo: getContainerTabPanelInfo,
  networkDeviceInfo: getNetworkDeviceTabPanelInfo,
  networkObjectInfo: (detailInfo) => Promise.resolve(getNetworkObjectTabPanelInfo(detailInfo)),
  networkInterface: getNetworkInterfaceTabPanelInfo,
  kafkaClusterInfo: getKafkaClusterTabPanelInfo,
  kafkaBrokerInfo: getKafkaBrokerTabPanelInfo,
  kafkaTopicInfo: getKafkaTopicTabPanelInfo,
  awsInfo: getCloudServiceTabPanelInfo,
  azureInfo: getCloudServiceTabPanelInfo,
  serviceInfo: getServiceTabPanelInfo,
  sqlInfo: getSqlTabPanelInfo,
  sessionInfo: getSessionTabPanelInfo,
  activeSessionInfo: getSessionTabPanelInfo,
  activeTxnList: getActiveTxnTabPanelInfo,
  cubridBrokerInfo: getBrokerTabPanelInfo,
  alertInfo: getAlertDetailPanelInfo,
  urlMonitoringInfo: getUrlMonitoringTabPanelInfo,
  statefulSetInfo: getStatefulSetTabPanelInfo,
  jobInfo: getJobTabPanelInfo,
  deploymentInfo: getDeploymentTabPanelInfo,
  daemonSetInfo: getDaemonSetTabPanelInfo,
  cronJobInfo: getCronJobTabPanelInfo,
  clusterInfo: getClusterTabPanelInfo,
  clusterRolesInfo: getClusterRolesTabPanelInfo,
  clusterRolesBindingsInfo: getClusterRolesBindingsTabPanelInfo,
  serviceAccountsInfo: getServiceAccountsTabPanelInfo,
  replicaSetInfo: getReplicaSetTabPanelInfo,
  configMapInfo: getConfigMapTabPanelInfo,
  roleBindingInfo: getRoleBindingTabPanelInfo,
  webserverInfo: getWebServerTabPanelInfo,
  openshiftRouteInfo: getOpenShiftRouteTabPanelInfo,
  serviceDetailInfo: getServiceDetailTabPanelInfo,
  // GPU는 이 legacy 경로(detailTabPanelInfo, 호출부 없음) 미지원 — 실제 연계는 어댑터(openGpuDetailByTrigger). 타입만 충족.
  gpuInfo: () => Promise.resolve(null),
};
